import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import { useApi } from '@/utils/axios'
import { NOTIFICACAO_ROTAS, ASSISTENCIA_TECNICA_ROTAS } from '@/utils/apiRoutes'

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const newMessagesCount = ref(0)
  const newRatingsCount = ref(0)
  const newMarketpriceCount = ref(0)
  const newAssistenciasCount = ref(0)
  const rankingUsers = ref([])
  const loading = reactive({
    notifications: false,
    marketprice: false,
    ranking: false,
    assistencias: false,
  })
  const error = reactive({
    notifications: null,
    marketprice: null,
    ranking: null,
    assistencias: null,
  })
  let notificationTimer = null

  // variables to track previous counts and send events only on change
  let prevMessagesCount = 0
  let prevRatingsCount = 0

  function dispatchNotificationEvent(eventName, detail = {}) {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent(eventName, { detail }))
    }
  }

  /**
   * Fetch counts for messages and ratings
   */
  const fetchNotificationsCount = async () => {
    loading.notifications = true
    error.notifications = null

    try {
      const notificacoesApi = useApi(NOTIFICACAO_ROTAS.QUANTIDADE_MENSAGENS_AVALIACOES)
      const response = await notificacoesApi.fetch()

      if (response && response.status === 'success' && response.data) {
        newMessagesCount.value = response.data.qtd_mensagens
        newRatingsCount.value = response.data.qtd_avaliacoes

        const msgs = Number(response.data.qtd_mensagens || 0)
        const rates = Number(response.data.qtd_avaliacoes || 0)

        // se diferente de 0 e diferente do anterior, dispara evento para views reagirem
        if (!!msgs && msgs !== prevMessagesCount) {
          dispatchNotificationEvent('notifications:new-messages', { count: msgs })
        }
        if (!!rates && rates !== prevRatingsCount) {
          dispatchNotificationEvent('notifications:new-ratings', { count: rates })
        }
        prevMessagesCount = msgs
        prevRatingsCount = rates
      }
    } catch (err) {
      error.notifications = err.message || 'Erro ao buscar notificações'
    } finally {
      loading.notifications = false
    }
  }

  /**
   * Fetch count for marketprice changes
   */
  const fetchMarketpriceCount = async () => {
    loading.marketprice = true
    error.marketprice = null

    try {
      const marketpriceApi = useApi(NOTIFICACAO_ROTAS.QUANTIDADE_MARKETPRICE)
      const response = await marketpriceApi.fetch()
      if (response && response.status === 'success' && response.data) {
        newMarketpriceCount.value = response.data.count
      }
    } catch (err) {
      error.marketprice = err.message || 'Erro ao buscar contagem do marketprice'
    } finally {
      loading.marketprice = false
    }
  }

  /**
   * Fetch count for open technical assistance requests
   */
  const fetchAssistenciasCount = async () => {
    loading.assistencias = true
    error.assistencias = null

    try {
      const assistenciasApi = useApi(ASSISTENCIA_TECNICA_ROTAS.QUANTIDADE_ABERTAS)
      const response = await assistenciasApi.fetch()

      if (response && response.status === 'success' && response.data) {
        newAssistenciasCount.value = response.data.total
      }
    } catch (err) {
      error.assistencias = err.message || 'Erro ao buscar contagem de assistências técnicas'
    } finally {
      loading.assistencias = false
    }
  }

  /**
   * Fetch user ranking data
   */
  const fetchUserRanking = async () => {
    loading.ranking = true
    error.ranking = null

    try {
      const rankingApi = useApi(NOTIFICACAO_ROTAS.RANKING_USUARIOS)
      const response = await rankingApi.fetch()

      if (response && response.status === 'success' && response.data) {
        rankingUsers.value = response.data
      }
    } catch (err) {
      error.ranking = err.message || 'Erro ao buscar ranking de usuários'
    } finally {
      loading.ranking = false
    }
  }

  /**
   * Fetch all notification data
   */
  const fetchAllNotificationData = async () => {
    await fetchNotificationsCount()
    await fetchMarketpriceCount()
    await fetchUserRanking()
    await fetchAssistenciasCount()
  }

  /**
   * Start notification polling
   * @param {number} interval - Polling interval in milliseconds
   */
  const startNotificationPolling = (interval = 120000) => {
    stopNotificationPolling()
    fetchAllNotificationData()

    notificationTimer = setInterval(() => {
      fetchAllNotificationData()
    }, interval)
  }

  /**
   * Stop notification polling
   */
  const stopNotificationPolling = () => {
    if (notificationTimer) {
      clearInterval(notificationTimer)
      notificationTimer = null
    }
  }

  return {
    // State
    newMessagesCount,
    newRatingsCount,
    newMarketpriceCount,
    newAssistenciasCount,
    rankingUsers,
    loading,
    error,

    // Methods
    fetchNotificationsCount,
    fetchMarketpriceCount,
    fetchAssistenciasCount,
    fetchUserRanking,
    fetchAllNotificationData,
    startNotificationPolling,
    stopNotificationPolling,
  }
})
